// Recommendation contracts (ADR 0009 §12), mirrored from the API. The flow:
// create (needs assessment) → score the gathered offers → the Suitability
// ranking → choose (non-cheapest requires the rationale) → [Tier-2 four-eyes via
// GA1] → present → decide.
export type RecommendationStatus =
  | "DRAFT"
  | "PENDING_TIER2"
  | "PRESENTED"
  | "DECIDED"
  | "ABANDONED";
export type CustomerDecision = "ACCEPTED" | "DECLINED" | "DEFERRED";
// The full core line set (catalog-taxonomy tri-pillar spine, ADR 0004 §4),
// grounded to the API's RECOMMENDATION_LINE_CODES: VEHICLE (trafik/kasko),
// DWELLING (dask/konut), PERSON/HEALTH (tss/oss), TRAVEL (seyahat).
export type LineCode = "trafik" | "kasko" | "dask" | "konut" | "tss" | "oss" | "seyahat";

export interface NeedsAssessment {
  mustHaves: string[];
  priorities?: string[];
  budgetPosture?: string;
}

export interface Recommendation {
  id: string;
  householdId: string;
  partyId: string | null;
  lineCode: string;
  insuredAssetId: string | null;
  needsAssessment: NeedsAssessment;
  status: RecommendationStatus;
  suggestedOfferId: string | null;
  chosenOfferId: string | null;
  reasonCode: string | null;
  cheaperButRationale: string | null;
  tier2Required: boolean;
  tier2ApprovalId: string | null;
  customerDecision: CustomerDecision | null;
  declineReason: string | null;
  advisorStaffId: string;
  decidedAt: string | null;
  createdAt: string;
}

/** One scored offer in the set — Suitability + its four server-computed
 *  components, the citations (quality snapshot + equivalence version), and the
 *  must-have hard-gate (mustHavesUnmet, when the offer fails a must-have). */
export interface RecommendationOffer {
  id: string;
  offerId: string;
  carrierId: string;
  packageVersionId: string;
  grossPremium: string;
  currency: string;
  suitability: string;
  needsFit: string;
  carrierQuality: string;
  coverageQualityVsNeeds: string;
  priceValue: string;
  carrierQualitySnapshotId: string;
  equivalenceVersion: number;
  mustHavesUnmet: string[] | null;
  rank: number;
  wasSuggested: boolean;
  wasChosen: boolean;
}

/** The customer's response to a presented proposal (I1 write → I2a read). Append-only
 *  from BOTH sides — even staff can't edit it; the console displays it, never edits. */
export interface CustomerProposalResponse {
  id: string;
  response: CustomerDecision;
  respondedAt: string;
}

export interface RecommendationDetail {
  recommendation: Recommendation;
  offers: RecommendationOffer[];
  // I2a fold-in — this rec's customer responses, NEWEST FIRST (index 0 = the latest;
  // a change-of-mind DEFERRED→ACCEPTED is preserved as history beneath it).
  customerResponses: CustomerProposalResponse[];
}

/** GET /v1/recommendations/pending-responses item — an in-scope PRESENTED rec the
 *  customer has responded to but the advisor hasn't bound. Drops off on decision(). */
export interface PendingResponse {
  recommendation: Recommendation;
  latestResponse: { response: CustomerDecision; respondedAt: string };
}

// ── GA1 four-eyes approval (the Tier-2 cycle) ──
export const RECOMMENDATION_TIER2 = "recommendation.tier2";
export type ApprovalStatus = "PENDING" | "APPROVED" | "REJECTED" | "CONSUMED";

/** A7 — where to review this approval, resolved SERVER-side per action.
 *  `null` means no surface exists yet for that action (see the API's
 *  review-path.ts for the list and why) — the inbox says so out loud. */
export interface ApprovalReview {
  app: "console" | "admin";
  path: string;
}

export interface Approval {
  id: string;
  action: string;
  resourceType: string | null;
  resourceId: string | null;
  requestedBy: string;
  approvedBy: string | null;
  status: ApprovalStatus;
  review?: ApprovalReview | null;
  expiresAt: string;
  createdAt: string;
}

/** The four Suitability components, in display order. */
export const SUITABILITY_COMPONENTS = [
  { key: "needsFit", labelKey: "rec.comp.needsFit" },
  { key: "carrierQuality", labelKey: "rec.comp.carrierQuality" },
  { key: "coverageQualityVsNeeds", labelKey: "rec.comp.coverageQuality" },
  { key: "priceValue", labelKey: "rec.comp.priceValue" },
] as const;
